import { PreApplicationDocType } from '../../../../core/applications/pre-application.types';
import { DocDropType } from '../../shared/doc-drop/doc-drop';

/** The three intake steps, in order. */
export type Step = 'bank' | 'financials' | 'offers';

/** How a document is weighted for the application. Drives the checklist tag. */
export type DocRequirement = 'required' | 'recommended';

/** A checklist document: the drop bucket + why it's asked for + how it's weighted. */
export interface ChecklistDoc {
  type: DocDropType<PreApplicationDocType>;
  /** Which step this document belongs to. */
  step: Extract<Step, 'bank' | 'financials'>;
  /** How load-bearing the document is — drives the tag + soft gating. */
  requirement: DocRequirement;
  /** One-line reason it's needed (client-safe). */
  why: string;
}

/**
 * The documents the pre-application asks for, grouped by step.
 *
 * - **Step 1 — Bank statements (required):** the floor for every funding
 *   decision; we ask for 6–9 months and encourage more.
 * - **Step 2 — Financial information (recommended):** the debtors age analysis
 *   and financial statements — optional but they sharpen the recommendation and
 *   pre-fill the real application, where full capture happens.
 */
export const CHECKLIST_DOCS: ChecklistDoc[] = [
  {
    type: {
      value: 'BANK_STATEMENT',
      label: 'Bank statements',
      hint: '6–9 months — the more you share, the better',
    },
    step: 'bank',
    requirement: 'required',
    why: 'Shows your cash flow — the basis for every funding decision.',
  },
  {
    type: {
      value: 'AGE_ANALYSIS',
      label: 'Age analysis',
      hint: 'Your current list of who owes you and for how long',
    },
    step: 'financials',
    requirement: 'recommended',
    why: 'Lets us advance cash against your unpaid invoices.',
  },
  {
    type: {
      value: 'FINANCIALS',
      label: 'Financial statements',
      hint: 'Profit & loss, balance sheet, cash flow statements',
    },
    step: 'financials',
    requirement: 'recommended',
    why: 'Strengthens your application and can improve your offer.',
  },
];

/** The tag label shown against a checklist document. */
export function requirementLabel(requirement: DocRequirement): string {
  return requirement === 'required' ? 'Required' : 'Recommended';
}
